import React from 'react';
import { Box, Typography, Button, Card, CardContent, Avatar, IconButton, Collapse } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { renderProtocolInstruction, isScannedTreatmentStep } from './spLot/StepsIns';
export interface ProcessingStep {
  id: number;
  name: string;
  status: string;
  icon?: React.ReactNode;
  instruction?: string;
  comment?: string | null;
  resourceId?: string;
  treatmentName?: string;
  equipments?: Array<{
    equipment_id: string;
    display_name?: string;
    is_mandatory?: boolean;
  }>;
  disabled?: boolean;
}

interface ProcessingStepsListProps {
  processingSteps: ProcessingStep[];
  isBulkView: boolean;
  expandedStep: number | null;
  currentStepId?: number;
  currentStepStatus?: string;
  stepRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  handleStepClick: (stepId: number) => void;
  handleAddComment: (stepId: number, existingComment?: string) => void;
  classes: any;
}

const ProcessingStepsList: React.FC<ProcessingStepsListProps> = ({
  processingSteps,
  isBulkView,
  expandedStep,
  currentStepId,
  currentStepStatus,
  stepRefs,
  handleStepClick,
  handleAddComment,
  classes,
}) => {
  const hasInProgressStep = processingSteps.some(step => step.status === 'In Progress');
  const firstNotStartedStep =processingSteps.find(step => step.status === 'Not Started');
  const isApiCurrentNotStarted = (currentStepStatus || '').toLowerCase() === 'not started';
  const hasApiCurrentStep = !!currentStepId;

  const getEquipmentDisplayValue = (step: ProcessingStep): string => {
    const isScanned = isScannedTreatmentStep(step.name);
    
    if (isScanned) {
      return String(step?.treatmentName ?? '').trim();
    }
    
    const equipmentId = String(step?.resourceId ?? '').trim();
    if (!equipmentId) return equipmentId;

    const stepEquipments = Array.isArray(step?.equipments) ? step.equipments : [];
    const matchedEquipment = stepEquipments.find((equipment: any) =>
      String(equipment?.equipment_id ?? '').trim().toLowerCase() === equipmentId.toLowerCase()
      && String(equipment?.display_name ?? '').trim() !== ''
    );

    return String(matchedEquipment?.display_name ?? equipmentId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {processingSteps
        .filter(step => {
          if (isBulkView) {
            if (expandedStep !== null) {
              return step.id === expandedStep;
            }
            const inProgressStep = processingSteps.find(s => s.status === 'In Progress');
            if (inProgressStep) {
              return step.id === inProgressStep.id;
            }
            const notStartedStep = processingSteps.find(s => s.status === 'Not Started');
            if (notStartedStep) {
              return step.id === notStartedStep.id;
            }
            return false;
          }
          return true;
        })
        .map((step) => {
          const resourceDisplayValue = getEquipmentDisplayValue(step);

          return (
          <Card
            key={step.id}
            ref={el => { stepRefs.current[step.id] = el; }}
            sx={{
              width: '100%',
              boxShadow: 'none',
              border: step.status === 'Completed'
                ? '1.5px solid #34C759'
                : step.status === 'Skipped' && expandedStep !== step.id
                  ? '1.5px solid var(--red-100, #D8010B)'
                  : step.status === 'Skipped'
                    ? '1.5px solid #FF3B30'
                    : step.status === 'In Progress'
                      ? '1.5px solid #FFB86A'
                      : step.status === 'Not Started' && expandedStep === step.id
                        ? '2px solid #3C3C3C'
                        : '1.5px solid #cac4c4',
              borderRadius: '8px',
              cursor: step.disabled ? 'not-allowed' : 'pointer',
              backgroundColor: step.status === 'Skipped' && expandedStep !== step.id
                ? '#FFFFFF'
                : step.status === 'Skipped' && expandedStep === step.id
                  ? '#FFF2F2'
                  : step.status === 'In Progress' && expandedStep === step.id
                    ? '#FFF9F1'
                    : step.status === 'Completed' && expandedStep === step.id
                      ? 'rgba(236, 253, 246, 1)'
                      : '#FFFFFF',
              opacity: step.disabled ? 0.5 : 1,
              gap: '10px',
              margin: '0 auto'
            }}
          >
            <CardContent sx={{ p: '14px 10px 14px 10px', '&:last-child': { pb: '14px' } }}>
              <Box
                sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', minWidth: 0 }}
                onClick={() => !step.disabled && handleStepClick(step.id)}
              >
                {/* Icon with number badge */}
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    sx={{
                      backgroundColor: step.status === 'In Progress' ? '#FFD594' : step.status === 'Completed' ? '#CAFFDD' : step.status === 'Skipped' ? '#FFCACA' : step.status === 'Not Started' ? '#E5E7EB' : '#f5f5f5',
                      color: '#666',
                      width: 48,
                      height: 48,
                      borderRadius: step.status === 'Not Started' ? 24 : '50%',
                      opacity: 1,
                      gap: step.status === 'Not Started' ? '10px' : undefined,
                      padding: step.status === 'Not Started' ? '8px' : undefined,
                      border: step.status === 'Completed' ? '1px solid #00A63E' : step.status === 'Not Started' ? '1px solid #808080' : step.status === 'In Progress' ? '1px solid #FE9A00' : step.status === 'Skipped' ? '1px solid #D8010B' : 'none',
                      '& svg': {
                        color: 'inherit'
                      },
                      '& img': {
                        filter: 'none'
                      }
                    }}
                  >
                    <Box sx={{ width: 24, height: 29, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto' }}>
                      {step.icon}
                    </Box>
                  </Avatar>
                  {/* Custom badge icon for skipped/completed/in-progress */}
                  {step.status === 'Skipped' && (
                    <img src="icons/skipped_icon.png" alt="Skipped" style={{ position: 'absolute', bottom: -4, right: -2, width: 20, height: 20 }} />
                  )}
                  {step.status === 'Completed' && (
                    <img src="icons/completed_icon.png" alt="Completed" style={{ position: 'absolute', bottom: -4, right: -2, width: 20, height: 20 }} />
                  )}
                  {step.status === 'Not Started' && (() => {
                    const isApiCurrentStep = !!currentStepId && currentStepId === step.id && isApiCurrentNotStarted;
                    const isLocalNextStep = !hasInProgressStep && firstNotStartedStep && firstNotStartedStep.id === step.id;

                    if (isApiCurrentStep || (!hasApiCurrentStep && isLocalNextStep)) {
                      return (
                        <img src="icons/upnext_icon.png" alt="Up Next" style={{ position: 'absolute', bottom: -4, right: -2, width: 20, height: 20 }} />
                      );
                    } else {
                      return (
                        <img src="icons/notstarted_icon.png" alt="Not Started" style={{ position: 'absolute', bottom: -4, right: -2, width: 20, height: 20 }} />
                      );
                    }
                  })()}
                  {step.status === 'In Progress' && (
                    <img src="icons/inprogres_icon.png" alt="In Progress" style={{ position: 'absolute', bottom: -4, right: -2, width: 20, height: 20 }} />
                  )}
                  {step.status !== 'Skipped' && step.status !== 'Completed' && step.status !== 'In Progress' && step.status !== 'Not Started' && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -2,
                      backgroundColor: 'white',
                      color: '#333',
                      border: '2px solid #e0e0e0',
                      fontWeight: 500,
                      fontSize: 15.6,
                      minWidth: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {step.id}
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    className={classes.labelNoto16Bold500_hh}
                    sx={{ color: step.status === 'In Progress' ? '#7E2A0C' : step.status === 'Completed' ? '#008236' : step.status === 'Skipped' ? '#C50009' : 'inherit', overflowWrap: 'anywhere' }}
                  >
                    {step.name}
                  </Typography>
                  {step.status !== 'Not Started' && resourceDisplayValue && (
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '1.1375rem', mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {isScannedTreatmentStep(step.name) ? 'Scanned Treatment' : 'Equipment ID'}:
                      </Box>{' '}
                      {resourceDisplayValue}
                    </Typography>
                  )}
                  {expandedStep !== step.id && (
                    <Box sx={{ display: 'flex', gap: '4px', mt: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {step.instruction && (
                        <IconButton size="small" disableRipple sx={{ p: 0, m: 0, minWidth: 0, minHeight: 0 }} onClick={e => e.stopPropagation()}>
                          <img src="icons/information_icon.png" alt="Information" style={{ width: 24, height: 24, display: 'block', margin: 0, padding: 0 }} />
                        </IconButton>
                      )}
                      {step.comment ? (
                        <IconButton size="small" disableRipple sx={{ p: 0, m: 0, minWidth: 0, minHeight: 0 }} onClick={e => e.stopPropagation()}>
                          <img src="icons/comment_icon.png" alt="Comment" style={{ width: 24, height: 24, display: 'block', margin: 0, padding: 0 }} />
                        </IconButton>
                      ) : (
                        <Button
                          startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '20.8px', width: '20.8px', height: '20.8px', color: '#1447E6' }} />}
                          onClick={e => {
                            e.stopPropagation();
                            handleAddComment(step.id);
                          }}
                          disableRipple
                          className={classes.labelNoto12Bold400_hh}
                          sx={{
                            color: '#1447E6',
                            backgroundColor: '#EFF6FF',
                            border: '0.5px solid #8EC5FF',
                            textTransform: 'none',
                            width: 'fit-content',
                            minWidth: '121px',
                            maxWidth: '100%',
                            p: '4px 10px',
                            height: 'auto',
                            minHeight: '28px',
                            borderRadius: '8px',
                            opacity: 1,
                            gap: '6px',
                            fontSize: '15.6px',
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiButton-startIcon': {
                              marginRight: 0,
                              marginLeft: 0
                            },
                            '&:hover': {
                              backgroundColor: '#dbeafe',
                              border: '0.5px solid #8EC5FF',
                              outline: 'none'
                            },
                            '&:focus': {
                              backgroundColor: '#dbeafe',
                              border: '0.5px solid #8EC5FF',
                              outline: 'none'
                            }
                          }}
                        >
                          Add Comment
                        </Button>
                      )}
                    </Box>
                  )}
                  <Collapse in={expandedStep === step.id}>
                    <Box sx={{ mt: '8px' }}>
                      {step.instruction && (
                        <Box sx={{ mb: '8px', p: '10px', backgroundColor: '#FFFCDD', borderRadius: '10px', border: '1px solid #CCB25B', minHeight: '52px', opacity: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <img src="icons/information_icon.png" alt="Information" />
                            <Typography variant="body2" className={classes.labelNoto12Bold400_hh}>
                              {renderProtocolInstruction(step.instruction, step.name)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {step.comment && (
                        <Box sx={{ p: '16px', backgroundColor: '#F6FAFF', borderRadius: '14px', border: '2px solid #B5D6F6', minHeight: '56px', opacity: 1, position: 'relative', color: '#23272F', fontSize: '20.8px', lineHeight: 1.6, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '12px' }}>
                          <Box sx={{ flexShrink: 0, mt: '2px' }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 26, color: '#1976D2', background: '#F6FAFF', borderRadius: '6px', boxSizing: 'content-box' }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" className={classes.labelNoto12Bold400_hh} sx={{ color: '#23272F', fontSize: '20.8px', fontWeight: 400, lineHeight: 1.6, mb: 0, p: 0, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {step.comment}
                            </Typography>
                            <Box sx={{ textAlign: 'right', mt: 1 }}>
                              <span className={classes.labelNoto12Bold400_hh} style={{ color: '#1976D2', fontWeight: 400, textDecoration: 'underline', cursor: 'pointer' }} onClick={e => {
                                e.stopPropagation();
                                handleAddComment(step.id, step.comment || '');
                              }}>
                                Edit
                              </span>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      {!step.comment && (
                        <Button
                          startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: '20.8px', width: '20.8px', height: '20.8px', color: '#1447E6' }} />}
                          onClick={e => {
                            e.stopPropagation();
                            handleAddComment(step.id);
                          }}
                          disableRipple
                          className={classes.labelNoto12Bold400_hh}
                          sx={{
                            color: '#1447E6',
                            backgroundColor: '#EFF6FF',
                            border: '0.5px solid #8EC5FF',
                            textTransform: 'none',
                            width: 'fit-content',
                            minWidth: '121px',
                            maxWidth: '100%',
                            p: '4px 10px',
                            height: 'auto',
                            minHeight: '28px',
                            borderRadius: '8px',
                            opacity: 1,
                            gap: '6px',
                            fontSize: '15.6px',
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiButton-startIcon': {
                              marginRight: 0,
                              marginLeft: 0
                            },
                            '&:hover': {
                              backgroundColor: '#dbeafe',
                              border: '0.5px solid #8EC5FF',
                              outline: 'none'
                            },
                            '&:focus': {
                              backgroundColor: '#dbeafe',
                              border: '0.5px solid #8EC5FF',
                              outline: 'none'
                            }
                          }}
                        >
                          Add Comment
                        </Button>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </CardContent>
          </Card>
          );
        })}
    </Box>
  );
};

export default ProcessingStepsList;
